/**
 * PRIVLUXE META PIXEL ANALYTICS
 * =============================
 * Pixel ID: 1574573198006422
 * 
 * PRIVACY COMMITMENT:
 * - NO message content is ever tracked
 * - NO personal data (email, phone, name) is sent to Facebook
 * - Only behavioral events for retargeting audiences
 * 
 * FUNNEL STAGES FOR RETARGETING:
 * ┌─────────────────────────────────────────────────────────────┐
 * │ Stage 1: VISITOR      → PageView                           │
 * │ Stage 2: ENGAGED      → ViewContent, ButtonClick           │
 * │ Stage 3: SIGNUP_START → InitiateCheckout, SignupStarted    │
 * │ Stage 4: SIGNUP_DONE  → CompleteRegistration               │
 * │ Stage 5: PROFILE_SET  → Lead, ProfileCompleted             │
 * │ Stage 6: INVITE_SENT  → AddToCart, InviteSent              │
 * │ Stage 7: FIRST_CHAT   → Purchase, FirstConversation        │
 * │ Stage 8: ACTIVE_USER  → Contact (for each message)         │
 * └─────────────────────────────────────────────────────────────┘
 * 
 * CUSTOM AUDIENCES YOU CAN CREATE IN ADS MANAGER:
 * 1. "Visited but didn't signup" - PageView but no InitiateCheckout
 * 2. "Started signup, didn't finish" - InitiateCheckout but no CompleteRegistration
 * 3. "Signed up, no profile" - CompleteRegistration but no Lead
 * 4. "Has profile, didn't invite" - Lead but no InviteSent
 * 5. "Sent invite, no conversation" - InviteSent but no FirstConversation
 * 6. "Active chatters" - FirstConversation or Contact events
 */

const META_PIXEL_ID = '1574573198006422';

// Check if gtag is available
const isGtagAvailable = () => typeof window.gtag === 'function';

// Check if Meta Pixel is available
const isFbqAvailable = () => typeof window.fbq === 'function';

// Check if Clarity is available
const isClarityAvailable = () => typeof window.clarity === 'function';

// Check if current page is an admin page (should not be tracked)
const isAdminPage = (pagePath) => {
  return pagePath && (pagePath.startsWith('/admin') || pagePath.includes('/admin/'));
};

/**
 * Stop Clarity tracking completely (for admin pages)
 */
export const stopClarityTracking = () => {
  if (isClarityAvailable()) {
    window.clarity("stop");
  }
};

/**
 * Set Clarity page for SPA navigation
 */
export const setClarityPage = (pagePath) => {
  if (isAdminPage(pagePath)) {
    stopClarityTracking();
    return;
  }
  
  if (isClarityAvailable()) {
    window.clarity("set", "page_path", pagePath);
    window.clarity("event", "pageview");
    const pageName = pagePath.replace(/\//g, '_').replace(/^_/, '') || 'home';
    window.clarity("upgrade", pageName);
  }
};

/**
 * Set Clarity user ID for user identification
 */
export const setClarityUser = (userId) => {
  if (isClarityAvailable() && userId) {
    window.clarity("set", "user_id", userId);
  }
};

// ============================================================
// PAGE VIEW & NAVIGATION TRACKING
// ============================================================

/**
 * Track page view with detailed data
 */
export const trackPageView = (pagePath, pageTitle) => {
  if (isAdminPage(pagePath)) {
    stopClarityTracking();
    return;
  }
  
  // GA4
  if (isGtagAvailable()) {
    window.gtag('event', 'page_view', {
      page_path: pagePath,
      page_title: pageTitle,
    });
  }
  
  // Meta Pixel - PageView
  if (isFbqAvailable()) {
    window.fbq('track', 'PageView');
  }
  
  // Clarity
  setClarityPage(pagePath);
  
  console.log('[Analytics] Page View:', pagePath);
};

/**
 * Track specific page visits for retargeting audiences
 */
export const trackPageVisit = (pageName, pageCategory) => {
  if (isFbqAvailable()) {
    window.fbq('trackCustom', 'PageVisit', {
      page_name: pageName,
      page_category: pageCategory,
      timestamp: new Date().toISOString(),
    });
  }
  
  if (isGtagAvailable()) {
    window.gtag('event', 'page_visit', {
      page_name: pageName,
      page_category: pageCategory,
    });
  }
};

// ============================================================
// CTA & BUTTON CLICK TRACKING
// ============================================================

/**
 * Track CTA button clicks with detailed context
 */
export const trackCTAClick = (buttonLocation, buttonText = 'Enter Privluxe') => {
  if (isGtagAvailable()) {
    window.gtag('event', 'cta_click', {
      event_category: 'engagement',
      button_location: buttonLocation,
      button_text: buttonText,
    });
  }
  
  // Meta Pixel - ViewContent for CTA clicks
  if (isFbqAvailable()) {
    window.fbq('track', 'ViewContent', {
      content_name: buttonText,
      content_category: buttonLocation,
      content_type: 'cta_button',
    });
  }
  
  console.log('[Analytics] CTA Click:', buttonLocation, buttonText);
};

/**
 * Track any button click for detailed heatmap data
 */
export const trackButtonClick = (buttonId, buttonText, pageName) => {
  if (isFbqAvailable()) {
    window.fbq('trackCustom', 'ButtonClick', {
      button_id: buttonId,
      button_text: buttonText,
      page_name: pageName,
      timestamp: new Date().toISOString(),
    });
  }
  
  if (isGtagAvailable()) {
    window.gtag('event', 'button_click', {
      button_id: buttonId,
      button_text: buttonText,
      page_name: pageName,
    });
  }
};

// ============================================================
// SIGNUP FUNNEL TRACKING
// ============================================================

/**
 * Track signup started (OTP requested)
 */
export const trackSignupStarted = (method = 'email') => {
  if (isGtagAvailable()) {
    window.gtag('event', 'signup_started', {
      event_category: 'conversion',
      method: method,
    });
  }
  
  // Meta Pixel - InitiateCheckout (signup started)
  if (isFbqAvailable()) {
    window.fbq('track', 'InitiateCheckout', {
      content_name: 'Signup',
      content_category: 'Registration',
      value: 0,
      currency: 'USD',
    });
    
    // Custom event for retargeting - Signup Started but not completed
    window.fbq('trackCustom', 'SignupStarted', {
      method: method,
      timestamp: new Date().toISOString(),
    });
  }
  
  console.log('[Analytics] Signup Started:', method);
};

/**
 * Track OTP sent
 */
export const trackOTPSent = (method = 'email') => {
  if (isFbqAvailable()) {
    window.fbq('trackCustom', 'OTPSent', {
      method: method,
      timestamp: new Date().toISOString(),
    });
  }
  
  if (isGtagAvailable()) {
    window.gtag('event', 'otp_sent', {
      method: method,
    });
  }
};

/**
 * Track OTP verified
 */
export const trackOTPVerified = (method = 'email') => {
  if (isFbqAvailable()) {
    window.fbq('trackCustom', 'OTPVerified', {
      method: method,
      timestamp: new Date().toISOString(),
    });
  }
  
  if (isGtagAvailable()) {
    window.gtag('event', 'otp_verified', {
      method: method,
    });
  }
};

/**
 * Track signup completed (account created)
 */
export const trackSignupCompleted = (method = 'email') => {
  if (isGtagAvailable()) {
    window.gtag('event', 'signup_completed', {
      event_category: 'conversion',
      method: method,
    });
  }
  
  // Meta Pixel - CompleteRegistration
  if (isFbqAvailable()) {
    window.fbq('track', 'CompleteRegistration', {
      content_name: 'Account Created',
      value: 1.00,
      currency: 'USD',
    });
    
    // Custom event for retargeting
    window.fbq('trackCustom', 'SignupCompleted', {
      method: method,
      timestamp: new Date().toISOString(),
    });
  }
  
  console.log('[Analytics] Signup Completed:', method);
};

/**
 * Track profile completed (username set)
 */
export const trackProfileCompleted = (username) => {
  if (isFbqAvailable()) {
    window.fbq('trackCustom', 'ProfileCompleted', {
      has_username: true,
      timestamp: new Date().toISOString(),
    });
    
    // Lead event - user is now a qualified lead
    window.fbq('track', 'Lead', {
      content_name: 'Profile Completed',
      content_category: 'Onboarding',
    });
  }
  
  if (isGtagAvailable()) {
    window.gtag('event', 'profile_completed', {
      event_category: 'conversion',
    });
  }
  
  console.log('[Analytics] Profile Completed');
};

/**
 * Track signup abandonment at specific step
 */
export const trackSignupAbandon = (step = 'unknown') => {
  if (isFbqAvailable()) {
    window.fbq('trackCustom', 'SignupAbandon', {
      abandon_step: step,
      timestamp: new Date().toISOString(),
    });
  }
  
  if (isGtagAvailable()) {
    window.gtag('event', 'signup_abandon', {
      event_category: 'conversion',
      abandon_step: step,
    });
  }
  
  console.log('[Analytics] Signup Abandoned at:', step);
};

// ============================================================
// INVITE TRACKING
// ============================================================

/**
 * Track invite page opened
 */
export const trackInvitePageOpened = () => {
  if (isFbqAvailable()) {
    window.fbq('trackCustom', 'InvitePageOpened', {
      timestamp: new Date().toISOString(),
    });
  }
};

/**
 * Track invite method selected (sms, email, link)
 */
export const trackInviteMethodSelected = (method) => {
  if (isFbqAvailable()) {
    window.fbq('trackCustom', 'InviteMethodSelected', {
      method: method,
      timestamp: new Date().toISOString(),
    });
  }
};

/**
 * Track invite sent
 */
export const trackInviteSent = (channel = 'unknown', sourceButton = 'unknown') => {
  if (isGtagAvailable()) {
    window.gtag('event', 'invite_sent', {
      event_category: 'conversion',
      invite_channel: channel,
      source_button: sourceButton,
    });
  }
  
  // Meta Pixel - Custom Event + Purchase equivalent
  if (isFbqAvailable()) {
    // Custom event for audience building
    window.fbq('trackCustom', 'InviteSent', {
      channel: channel,
      source: sourceButton,
      timestamp: new Date().toISOString(),
    });
    
    // AddToCart as a proxy for "valuable action"
    window.fbq('track', 'AddToCart', {
      content_name: 'Invite Sent',
      content_category: channel,
      value: 0.50,
      currency: 'USD',
    });
  }
  
  console.log('[Analytics] Invite Sent:', channel, sourceButton);
};

/**
 * Track invite skipped (user skipped onboarding invite)
 */
export const trackInviteSkipped = () => {
  if (isFbqAvailable()) {
    window.fbq('trackCustom', 'InviteSkipped', {
      timestamp: new Date().toISOString(),
    });
  }
  
  if (isGtagAvailable()) {
    window.gtag('event', 'invite_skipped', {
      event_category: 'onboarding',
    });
  }
};

/**
 * Track invite accepted (by recipient)
 */
export const trackInviteAccepted = () => {
  if (isFbqAvailable()) {
    window.fbq('trackCustom', 'InviteAccepted', {
      timestamp: new Date().toISOString(),
    });
    
    // This is a conversion - someone joined via invite
    window.fbq('track', 'Subscribe', {
      value: 1.00,
      currency: 'USD',
    });
  }
};

// ============================================================
// CONVERSATION & MESSAGING TRACKING
// ============================================================

/**
 * Track first conversation started
 */
export const trackFirstConversation = () => {
  if (isFbqAvailable()) {
    window.fbq('trackCustom', 'FirstConversation', {
      timestamp: new Date().toISOString(),
    });
    
    // Purchase event - user has completed full funnel
    window.fbq('track', 'Purchase', {
      content_name: 'First Conversation',
      value: 2.00,
      currency: 'USD',
    });
  }
  
  if (isGtagAvailable()) {
    window.gtag('event', 'first_conversation', {
      event_category: 'conversion',
    });
  }
  
  console.log('[Analytics] First Conversation Started');
};

/**
 * Track message sent (NO MESSAGE CONTENT - only counts)
 * This is for engagement metrics only
 */
export const trackMessageSent = (isFirstMessage = false, messageType = 'text') => {
  // PRIVACY: We only track that a message was sent, NOT its content
  if (isGtagAvailable()) {
    window.gtag('event', 'message_sent', {
      event_category: 'engagement',
      is_first_message: isFirstMessage,
      message_type: messageType, // 'text', 'voice', 'image', 'video' - NO content
    });
  }
  
  if (isFbqAvailable()) {
    // Track message count only - no content, no recipient, no details
    window.fbq('trackCustom', 'MessageSent', {
      is_first: isFirstMessage,
      type: messageType,
    });
    
    // First message = user completed full funnel
    if (isFirstMessage) {
      window.fbq('track', 'Contact', {
        content_name: 'First Message',
      });
    }
  }
  
  console.log('[Analytics] Message Event (no content tracked):', messageType);
};

/**
 * Track voice note recorded
 */
export const trackVoiceNoteRecorded = () => {
  if (isFbqAvailable()) {
    window.fbq('trackCustom', 'VoiceNoteRecorded', {
      timestamp: new Date().toISOString(),
    });
  }
  trackFeatureUsed('voice_note');
};

/**
 * Track media sent (image/video)
 */
export const trackMediaSent = (mediaType, isViewOnce = false) => {
  if (isFbqAvailable()) {
    window.fbq('trackCustom', 'MediaSent', {
      media_type: mediaType,
      view_once: isViewOnce,
      timestamp: new Date().toISOString(),
    });
  }
  trackFeatureUsed(`media_${mediaType}`);
};

// ============================================================
// CALL TRACKING
// ============================================================

/**
 * Track call initiated
 */
export const trackCallInitiated = (callType = 'voice') => {
  if (isFbqAvailable()) {
    window.fbq('trackCustom', 'CallInitiated', {
      call_type: callType,
      timestamp: new Date().toISOString(),
    });
  }
  
  if (isGtagAvailable()) {
    window.gtag('event', 'call_initiated', {
      call_type: callType,
    });
  }
};

/**
 * Track call completed
 */
export const trackCallCompleted = (callType = 'voice', durationSeconds = 0) => {
  if (isFbqAvailable()) {
    window.fbq('trackCustom', 'CallCompleted', {
      call_type: callType,
      duration: durationSeconds,
      timestamp: new Date().toISOString(),
    });
  }
};

// ============================================================
// FEATURE USAGE TRACKING
// ============================================================

/**
 * Track feature usage
 */
export const trackFeatureUsed = (featureName) => {
  if (isGtagAvailable()) {
    window.gtag('event', 'feature_used', {
      event_category: 'engagement',
      feature: featureName,
    });
  }
  
  if (isFbqAvailable()) {
    window.fbq('trackCustom', 'FeatureUsed', {
      feature: featureName,
      timestamp: new Date().toISOString(),
    });
  }
  
  console.log('[Analytics] Feature Used:', featureName);
};

// ============================================================
// LOGIN TRACKING
// ============================================================

/**
 * Track login
 */
export const trackLogin = (method = 'email') => {
  if (isGtagAvailable()) {
    window.gtag('event', 'login', {
      event_category: 'engagement',
      method: method,
    });
  }
  
  if (isFbqAvailable()) {
    window.fbq('trackCustom', 'UserLogin', {
      method: method,
      timestamp: new Date().toISOString(),
    });
  }
  
  console.log('[Analytics] Login:', method);
};

// ============================================================
// ERROR TRACKING
// ============================================================

/**
 * Track errors
 */
export const trackError = (errorType, errorMessage) => {
  if (isGtagAvailable()) {
    window.gtag('event', 'error', {
      event_category: 'error',
      error_type: errorType,
      error_message: errorMessage,
    });
  }
  
  if (isFbqAvailable()) {
    window.fbq('trackCustom', 'ErrorOccurred', {
      error_type: errorType,
      error_message: errorMessage,
      timestamp: new Date().toISOString(),
    });
  }
  
  console.log('[Analytics] Error:', errorType, errorMessage);
};

// ============================================================
// SCROLL & ENGAGEMENT TRACKING
// ============================================================

/**
 * Track scroll depth
 */
export const trackScrollDepth = (percent) => {
  if (isGtagAvailable()) {
    window.gtag('event', 'scroll_depth', {
      event_category: 'engagement',
      percent: percent,
    });
  }
  
  if (isFbqAvailable()) {
    window.fbq('trackCustom', 'ScrollDepth', {
      percent: percent,
    });
  }
  
  console.log('[Analytics] Scroll Depth:', percent + '%');
};

/**
 * Initialize scroll tracking
 */
export const initScrollTracking = () => {
  let scrollTracked = {};
  
  const handleScroll = () => {
    const scrollPercent = Math.round(
      (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100
    );
    
    [25, 50, 75, 100].forEach((threshold) => {
      if (scrollPercent >= threshold && !scrollTracked[threshold]) {
        scrollTracked[threshold] = true;
        trackScrollDepth(threshold);
      }
    });
  };
  
  window.addEventListener('scroll', handleScroll, { passive: true });
  
  return () => window.removeEventListener('scroll', handleScroll);
};

/**
 * Initialize time tracking
 */
export const initTimeTracking = () => {
  const startTime = Date.now();
  
  const handleUnload = () => {
    const timeSpent = Math.round((Date.now() - startTime) / 1000);
    
    if (isGtagAvailable()) {
      window.gtag('event', 'time_on_page', {
        event_category: 'engagement',
        seconds: timeSpent,
      });
    }
    
    if (isFbqAvailable()) {
      window.fbq('trackCustom', 'TimeOnPage', {
        seconds: timeSpent,
      });
    }
  };
  
  window.addEventListener('beforeunload', handleUnload);
  
  return () => window.removeEventListener('beforeunload', handleUnload);
};

// ============================================================
// USER IDENTIFICATION
// ============================================================

/**
 * Set user data for tracking (after login/signup)
 */
export const setUserData = (userId, email = null, phone = null) => {
  // GA4 user properties
  if (isGtagAvailable()) {
    window.gtag('set', 'user_properties', {
      user_id: userId,
    });
  }
  
  // Clarity user ID
  setClarityUser(userId);
  
  console.log('[Analytics] User Identified:', userId);
};

export default {
  trackPageView,
  trackPageVisit,
  trackCTAClick,
  trackButtonClick,
  trackSignupStarted,
  trackOTPSent,
  trackOTPVerified,
  trackSignupCompleted,
  trackProfileCompleted,
  trackSignupAbandon,
  trackInvitePageOpened,
  trackInviteMethodSelected,
  trackInviteSent,
  trackInviteSkipped,
  trackInviteAccepted,
  trackFirstConversation,
  trackMessageSent,
  trackVoiceNoteRecorded,
  trackMediaSent,
  trackCallInitiated,
  trackCallCompleted,
  trackFeatureUsed,
  trackLogin,
  trackError,
  trackScrollDepth,
  initScrollTracking,
  initTimeTracking,
  setUserData,
  setClarityPage,
  setClarityUser,
  stopClarityTracking,
};
